import React, { useEffect, useState, useCallback } from 'react';
import { getGallery } from '../services/api';
import './Gallery.css';

const ALL = 'Tout';
const PAGE_SIZE = 12;

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState(ALL);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    getGallery()
      .then(setImages)
      .finally(() => setLoading(false));
  }, []);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [filter]);

  // Keyboard close lightbox
  const closeLightbox = useCallback(() => setLightbox(null), []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeLightbox]);

  const categories = [ALL, ...Array.from(new Set(images.map((i) => i.category).filter(Boolean))).sort()];
  const filtered = filter === ALL ? images : images.filter((i) => i.category === filter);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function goPage(p) {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="gallery-page">
      {/* Page Banner */}
      <section className="page-banner">
        <div className="container">
          <h1 className="page-banner__title">Galerie</h1>
          <p className="page-banner__sub">Des moments précieux partagés en communauté</p>
        </div>
      </section>

      <div className="container gallery-container">

        {/* Filters */}
        <div className="gallery-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`gallery-filter-btn${filter === cat ? ' gallery-filter-btn--active' : ''}`}
              onClick={() => setFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <p className="gallery-loading">Chargement...</p>
        ) : visible.length === 0 ? (
          <p className="gallery-empty">Aucune image trouvée.</p>
        ) : (
          <>
            <div className="gallery-grid">
              {visible.map((img) => (
                <button
                  key={img.id}
                  className="gallery-item"
                  onClick={() => setLightbox({ src: img.image_url, title: img.title, description: img.description })}
                  aria-label={`Voir ${img.title}`}
                >
                  <img src={img.image_url} alt={img.title} className="gallery-item__img" loading="lazy" />
                  <div className="gallery-item__overlay">
                    <span className="gallery-item__title">{img.title}</span>
                    {img.category && <span className="gallery-item__cat badge badge-gold">{img.category}</span>}
                  </div>
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="pagination__btn" disabled={page === 1} onClick={() => goPage(page - 1)} aria-label="Page précédente">‹</button>
                {buildPages(page, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`el-${i}`} className="pagination__ellipsis">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`pagination__btn${p === page ? ' pagination__btn--active' : ''}`}
                      onClick={() => goPage(p)}
                      aria-current={p === page ? 'page' : undefined}
                    >{p}</button>
                  )
                )}
                <button className="pagination__btn" disabled={page === totalPages} onClick={() => goPage(page + 1)} aria-label="Page suivante">›</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <button className="lightbox__close" onClick={closeLightbox} aria-label="Fermer">✕</button>
          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.title} className="lightbox__img" />
            <div className="lightbox__caption">
              <p className="lightbox__title">{lightbox.title}</p>
              {lightbox.description && <p className="lightbox__desc">{lightbox.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildPages(current, total) {
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }
  return pages;
}
